/**
 * Industry starter packs and recurring presets (tracker item 76). Mirror
 * backend modules/starter-templates/starter-templates.types.ts and
 * modules/recurring-presets/recurring-presets.types.ts as the controllers
 * return them (dates are ISO strings).
 */

export const STARTER_PACK_KEYS = ['lawn_care', 'pest_control', 'hvac', 'cleaning', 'pool_service'] as const;
export type StarterPackKey = (typeof STARTER_PACK_KEYS)[number];

/** The record types a pack flags as "Starter" (starter_seeded_records.entityType). */
export const STARTER_ENTITY_TYPES = [
  'ITEM',
  'PRICE_CHART',
  'CHECKLIST_TEMPLATE',
  'LEAD_SOURCE',
  'RECURRING_PRESET',
  'NOTIFICATION_TEMPLATE',
] as const;
export type StarterEntityType = (typeof STARTER_ENTITY_TYPES)[number];

/** A preview / apply entry is about a flagged type or an item category (created when missing, never flagged). */
export type StarterPlanEntityType = StarterEntityType | 'ITEM_CATEGORY';

/** Why a record is not created. */
export const STARTER_SKIP_REASONS = ['EXISTS', 'EXISTS_DELETED', 'CUSTOMIZED', 'ACCOUNT_MISSING'] as const;
export type StarterSkipReason = (typeof STARTER_SKIP_REASONS)[number];

export const STARTER_KEEP_REASONS = ['EDITED', 'IN_USE'] as const;
export type StarterKeepReason = (typeof STARTER_KEEP_REASONS)[number];

export interface StarterPackCounts {
  items: number;
  priceCharts: number;
  checklistTemplates: number;
  notificationOverrides: number;
  leadSources: number;
  recurringPresets: number;
}

/** GET /starter-templates */
export interface StarterPackSummary {
  key: StarterPackKey;
  title: string;
  description: string;
  counts: StarterPackCounts;
  /** The tenant has records flagged from this pack */
  applied: boolean;
  /** Flagged records of this pack still in the tenant */
  seededCount: number;
}

// ---- GET /starter-templates/:packKey (the pack's contents) ----

export interface StarterPackItem {
  name: string;
  description?: string;
  itemType: string;
  /** Placeholder price (decimal string) */
  unitPrice: string;
  unitCost?: string;
  unitOfMeasure: string;
  taxable: boolean;
  category: string;
  revenueAccountNumber: string;
  expenseAccountNumber?: string;
  defaultDurationMinutes?: number;
  reorderPoint?: string;
  priceChart?: string;
  checklistTemplate?: string;
}

export interface StarterPackPriceChart {
  name: string;
  description?: string;
  unit: string;
  pricingType: string;
  overageRatePerUnit: string;
  tiers: Array<{ upToSize: string; price: string }>;
}

export interface StarterPackChecklistTemplate {
  name: string;
  steps: Array<{ text: string; required: boolean }>;
}

export interface StarterPackNotificationOverride {
  eventKey: string;
  subject: string;
  body: string;
}

export interface StarterPackLeadSource {
  name: string;
  description?: string;
}

export interface StarterPackRecurringPreset {
  name: string;
  frequency: string;
  interval: number;
  frequencyDetails?: {
    daysOfWeek?: number[];
    dayOfMonth?: number;
    ordinal?: number;
    dayOfWeek?: number;
  };
  billingModel: string;
  billingFrequency?: string;
  billingInterval?: number;
  notes?: string;
}

export interface StarterPack {
  key: StarterPackKey;
  title: string;
  description: string;
  items: StarterPackItem[];
  priceCharts: StarterPackPriceChart[];
  checklistTemplates: StarterPackChecklistTemplate[];
  notificationOverrides: StarterPackNotificationOverride[];
  leadSources: StarterPackLeadSource[];
  recurringPresets: StarterPackRecurringPreset[];
}

// ---- preview / apply ----

export interface StarterPlanEntry {
  entityType: StarterPlanEntityType;
  /** The record's name (an event key for notification wording) */
  name: string;
  /** 'restore': an item that a starter "remove" soft-deleted comes back */
  action: 'create' | 'skip' | 'restore';
  reason: StarterSkipReason | null;
}

export interface StarterPreviewResponse {
  packKey: StarterPackKey;
  willCreate: number;
  /** Items a starter remove deleted that apply brings back (absent on a server without restore: read as 0) */
  willRestore?: number;
  willSkip: number;
  entries: StarterPlanEntry[];
}

export interface StarterAppliedEntry extends StarterPlanEntry {
  /** The created or restored record's id; null for skipped entries */
  id: string | null;
}

export interface StarterApplyResponse {
  packKey: StarterPackKey;
  created: StarterAppliedEntry[];
  /** Items brought back (absent on a server without restore: read as none) */
  restored?: StarterAppliedEntry[];
  skipped: StarterAppliedEntry[];
}

// ---- seeded records, Keep, remove ----

export interface SeededRecord {
  /** starter_seeded_records.id (what Keep takes) */
  id: string;
  entityType: StarterEntityType;
  /** The id of the item, chart, template, lead source or preset */
  entityId: string;
  name: string;
  seededAt: string;
  /** Changed after seeding */
  edited: boolean;
}

export interface SeededPack {
  packKey: StarterPackKey;
  title: string;
  records: SeededRecord[];
}

export interface SeededRecordsResponse {
  packs: SeededPack[];
}

export interface StarterKeepResponse {
  id: string;
  entityType: StarterEntityType;
  entityId: string;
}

export interface StarterRemoveEntry {
  entityType: StarterEntityType;
  name: string;
}

export interface StarterRemoveResponse {
  packKey: StarterPackKey;
  removed: StarterRemoveEntry[];
  kept: Array<StarterRemoveEntry & { reason: StarterKeepReason }>;
}

/** Backend error code of the starter routes (404). */
export const STARTER_ERROR_CODES = { unknownPack: 'STARTER_PACK_UNKNOWN' } as const;

// ---- recurring presets ----

export interface RecurringPreset {
  id: string;
  name: string;
  frequency: string;
  interval: number;
  frequencyDetails: {
    daysOfWeek?: number[];
    dayOfMonth?: number;
    ordinal?: number;
    dayOfWeek?: number;
  } | null;
  billingModel: 'PER_VISIT' | 'FLAT_CONTRACT';
  /** FLAT_CONTRACT only */
  billingFrequency: 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'ANNUALLY' | null;
  billingInterval: number;
  notes: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateRecurringPresetRequest {
  name: string;
  frequency: string;
  interval?: number;
  frequencyDetails?: RecurringPreset['frequencyDetails'];
  billingModel?: 'PER_VISIT' | 'FLAT_CONTRACT';
  billingFrequency?: RecurringPreset['billingFrequency'];
  billingInterval?: number;
  notes?: string | null;
  isActive?: boolean;
}

/** PATCH body: omitted fields are unchanged; null clears frequencyDetails, billingFrequency and notes. */
export type UpdateRecurringPresetRequest = Partial<CreateRecurringPresetRequest>;
